// D3-1 / D3-2 — THE SUPPORT VIEW, AND THE WALLS. THE LAW.
//
// She needs to see a member's REPORTS exactly as that member sees them, so "it looks broken" is
// diagnosable. She must never be able to see that member's inner life. Those two sentences are in
// tension, and everything in this file exists to hold the line between them in ONE place that a gate
// can run, rather than in thirty route handlers where one omission is a privacy breach.
//
// THE LAW IS AN ALLOWLIST, NOT A BLOCKLIST, AND THAT IS THE WHOLE DESIGN.
// A blocklist ("do not return journals") fails silently the day somebody adds a new personal
// surface: the new thing is not on the list, so it leaks. An allowlist fails CLOSED: a surface
// nobody has explicitly opened stays shut, including one invented next year.
//
// Imports nothing, so `scripts/d3-support-walls.mjs` runs it and the server enforces with it.

// ---------------------------------------------------------------------------
// WHAT THE SUPPORT VIEW MAY SHOW. Exactly this, and nothing else, ever.
// ---------------------------------------------------------------------------
export const SUPPORT_ALLOWED = [
  "reports",        // the member's generated readings, rendered as she sees them
  "profile-masked", // year of birth + city only, per D3-2
  "usage-counts",   // how many of each thing, no content
];

// ---------------------------------------------------------------------------
// WHAT IT MAY NEVER SHOW. Named explicitly TOO, even though the allowlist already excludes them,
// because a named refusal is testable and a silent absence is not. Every one of these is a thing the
// order says NEVER, and the fixture asserts each by name.
// ---------------------------------------------------------------------------
export const SUPPORT_FORBIDDEN = [
  "journal",          // journal entries and notepaper: never
  "notepaper",
  "lala-conversation", // Ask Lala conversation content: never (D3-6's transcripts are a SEPARATE
                       // surface with its own passcode gate and its own ledger; not this one)
  "bookmarks",        // bookmark names
  "saved-verses",
  "shelf-notes",      // personal notes on shelf items
  "birth-full",       // full birthdate and birth time
];

export const isSupportAllowed = (surface) => SUPPORT_ALLOWED.includes(String(surface || ""));
export const isSupportForbidden = (surface) => SUPPORT_FORBIDDEN.includes(String(surface || ""));

// THE ONE QUESTION EVERY SUPPORT ROUTE ASKS. Unknown surfaces FAIL CLOSED: a surface nobody
// registered is refused, not accidentally served.
export function supportMay(surface) {
  return isSupportAllowed(surface);
}

// ---------------------------------------------------------------------------
// D3-2 — THE BIRTH-DATA MASK. "The profile pane shows YEAR AND CITY ONLY, not full birthdate/time."
//
// The REPORT shows what the report shows — that is her explicit carve-out, and it is right: a chart
// is unreadable without its own header, and diagnosing "my chart is wrong" requires seeing the chart.
// The PROFILE PANE beside it is the surface that gets masked.
// ---------------------------------------------------------------------------
export function maskProfile(profile = {}) {
  const year = (() => {
    const raw = profile.birthDate || profile.birth_date || "";
    const m = String(raw).match(/(\d{4})/);
    return m ? m[1] : "";
  })();
  return {
    // Enough to tell two members apart and to sanity-check a chart's ballpark.
    name: profile.name || profile.first_name || "",
    birthYear: year,
    birthCity: profile.birthplace || profile.birth_city || "",
    birthCountry: profile.birth_country || profile.birthCountry || "",
    // Said out loud on the pane, so she is never guessing whether she is seeing everything.
    masked: true,
    maskNote: "Year and city only. The full birth date and time are not shown here; open the full member detail if you need them, and that opens with its own log line.",
  };
}

// A mask that leaks is worse than no mask, because it is trusted. The gate runs this.
const FULL_DATE = /\b\d{4}-\d{2}-\d{2}\b|\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/;
const CLOCK_TIME = /\b\d{1,2}:\d{2}\s*(am|pm)?\b/i;

export function maskViolations(masked = {}) {
  const issues = [];
  const blob = JSON.stringify(masked || {});
  if (FULL_DATE.test(blob)) issues.push("a full birth date survived the mask");
  if (CLOCK_TIME.test(blob)) issues.push("a birth time survived the mask");
  for (const key of ["birthDate", "birth_date", "birthTime", "birth_time", "birthPeriod"]) {
    if (masked[key] !== undefined) issues.push(`the masked profile still carries ${key}`);
  }
  return issues;
}

// ---------------------------------------------------------------------------
// D3-1 — READ-ONLY, AND PROVED RATHER THAN PROMISED.
//
// "Nothing in this view can edit, delete, regenerate or send anything on their account." The way to
// make that true is not to hide the buttons. It is for the support namespace to have no verb that
// writes: the fixture asserts every support route is a GET except the two that manage the passcode
// and the ones that WRITE THE LEDGER, which write only our own audit rows and never the member's.
// ---------------------------------------------------------------------------
export const SUPPORT_WRITE_ROUTES = [
  "/api/owner/support/unlock",   // sets the passcode cookie, writes no member data
  "/api/owner/support/passcode", // sets HER passcode, writes no member data
];

export const isSupportWriteRoute = (url) => SUPPORT_WRITE_ROUTES.includes(String(url || "").split("?")[0]);

// ---------------------------------------------------------------------------
// THE COPY. Plain, and honest about what is happening, because the member-facing disclosure already
// exists in the Terms and this is the owner-facing half of the same truth.
// ---------------------------------------------------------------------------
export const PASSCODE_PROMPT = "Enter your support passcode to open member views.";
// >>> DOOR-1 (2026-08-21) — THIS SENTENCE POINTED AT A PLACE THAT DOES NOT EXIST. <<< It read
// *"Set one in dashboard settings before member views will open."* and **there is no dashboard
// settings surface anywhere in this house** — measured, and reported by the window before this one.
// A2's dead box wearing words rather than a button, and #127's own defect one door over: a sentence
// describing a control that is not there. **She sets it HERE now**, on the locked screen itself, so
// the sentence names the thing directly in front of her rather than sending her hunting.
export const PASSCODE_UNSET = "No passcode is set yet. Choose one here and this room is yours.";
export const PASSCODE_WRONG = "That passcode is not right.";
export const LEDGER_NOTE = "Every member view you open is written to the action log: who, what, when. It cannot be edited or deleted.";
export const READ_ONLY_NOTE = "Read-only. Nothing here can change, delete, regenerate or send anything on their account.";

// ===============================================================================================
// >>> DOOR-1 (2026-08-21) — HER OWN DOOR. SHE NEVER HUNTS A SETTINGS MENU FOR HER OWN ROOM. <<<
//
// **THE SERVER HALF HAS EXISTED SINCE D3-3 AND HAD NO DOOR IN HER ROOM.** `POST
// /api/owner/support/passcode` sets AND rotates, hashes, requires the CURRENT passcode once one
// exists, ledgers `support.passcode-set`, and deliberately does not silently unlock the session that
// changed it. The Teaching Room's locked screen drew ONE control — an unlock field — and **branched
// on `passcodeSet` nowhere at all**, so a first run met a prompt for a passcode that did not exist
// and there was no rotate anywhere. R38/R165's trap in its purest form: hashed, ledgered, wired to
// nothing.
//
// **FOUR STATES, NEVER TWO (R18/L5.1), AND THE FIRST IS THE ONE A NAIVE BUILD LOSES.** `wait` is
// *we have not asked yet* and draws NOTHING — collapsed into `set`, a first-run SETTER flashes at an
// owner who has had a passcode for months, on every load of her own room. `set` is a first run and
// takes no current passcode because there is none to take. `unlock` is today's screen. `open` is
// through.
//
// **PRESENCE, NEVER TRUTHINESS (R53/R136/R137).** Both flags arrive off a JSON payload where
// `Boolean("false")` is `true`, and a coerced read here either shows a setter to an owner whose
// passcode is set (which the server would then refuse for want of the current one, reading as a
// broken room) or hides it from one who has none.
export const PASSCODE_DOOR = Object.freeze({ WAIT: "wait", SET: "set", UNLOCK: "unlock", OPEN: "open" });

export function passcodeDoorState(gate) {
  // >>> THE `typeof gate !== "object"` HALF CAME OUT, BECAUSE IT COULD NOT CHANGE AN ANSWER. <<<
  // Its own mutation came back VACUOUS and the run was right (R63/R134): DRIVEN over a string, a
  // number, a boolean, a function and a symbol, every one reaches WAIT either way, because a
  // property read on a non-object is `undefined` and the two `=== true` tests below refuse it. **A
  // line that cannot change an answer is a line no mutation can reach and no reader can trust**
  // (R216's ruling), so it is removed from the LAW rather than the mutation being aimed around it.
  if (!gate) return PASSCODE_DOOR.WAIT;
  if (gate.unlocked === true) return PASSCODE_DOOR.OPEN;
  if (gate.passcodeSet === true) return PASSCODE_DOOR.UNLOCK;
  if (gate.passcodeSet === false) return PASSCODE_DOOR.SET;
  return PASSCODE_DOOR.WAIT;
}

/** The server's own floor, so the form cannot offer what the door refuses (R15's shape). It is the
 *  ONE number: a form that asked for five would be refused at the door with a sentence about six. */
export const PASSCODE_MIN = 6;

/** >>> HER *CHANGEABLE ANYTIME* CLAUSE IS THE ROTATE, AND IT IS A DIFFERENT ACT FROM THE FIRST RUN
 *  (R18). <<< A first run has no current passcode to prove; a rotate MUST prove one, or a borrowed
 *  laptop with a live owner cookie silently replaces the second key — which is the whole reason
 *  D3-3's server asks for it. Two modes, one law, so the form and the door cannot disagree about
 *  which one this is. */
export function passcodeSubmitVerdict(input) {
  // >>> A LAW THAT THROWS IS WORSE THAN ONE THAT REFUSES, AND THIS ONE DID (R19). <<< It was written
  // `({ mode, current = "", ... } = {})`, and a default only applies to `undefined` — so
  // `passcodeSubmitVerdict(null)` threw a destructuring error rather than answering. This is
  // exported and reached from a render, so a throw takes her whole room down rather than refusing
  // one tap. Its own fixture caught it on the first run.
  const { mode, current = "", next = "", confirm = "" } = input && typeof input === "object" ? input : {};
  if (mode !== "set" && mode !== "rotate") {
    return { ok: false, reason: "mode", message: "That is not a passcode change." };
  }
  const now = typeof current === "string" ? current : "";
  const want = typeof next === "string" ? next : "";
  const again = typeof confirm === "string" ? confirm : "";
  // A rotate proves the current one FIRST, or she types a whole new passcode twice and is then told
  // the thing she could have been told before she started.
  if (mode === "rotate" && !now) {
    return { ok: false, reason: "current", message: "Type your current passcode first." };
  }
  if (!want) return { ok: false, reason: "next", message: "Choose a passcode." };
  if (want.length < PASSCODE_MIN) {
    return { ok: false, reason: "short", message: `Use at least ${PASSCODE_MIN} characters.` };
  }
  // Typed twice, because there is no way back: a mistyped passcode on a first run locks her out of
  // her own room with no recovery path on this site at all.
  if (!again) return { ok: false, reason: "confirm", message: "Type it once more to be sure." };
  if (again !== want) return { ok: false, reason: "match", message: "Those two do not match." };
  if (mode === "rotate" && now === want) {
    return { ok: false, reason: "same", message: "That is the passcode you already have." };
  }
  return { ok: true, reason: "", message: "" };
}

/** What the POST carries. A first run sends NO `current` at all rather than an empty string, because
 *  the server branches on whether a passcode exists and a caller may not assert that either way. */
export function passcodeSubmitBody({ mode, current = "", next = "" } = {}) {
  return mode === "rotate" ? { current: String(current), next: String(next) } : { next: String(next) };
}

export const PASSCODE_SET_LABEL = "Set my passcode";
export const PASSCODE_ROTATE_LABEL = "Change my passcode";
export const PASSCODE_SET_LEDE = "This is the second key to this room. Choose it here and it is yours.";
export const PASSCODE_ROTATE_LEDE = "Change it whenever you like. You will need the one you have now.";

// D3-1 — HER WORDS FOR THE DOOR, IN ONE PLACE. The button text is in the law rather than typed into
// the dashboard, so the rendered-DOM gate asserts the SCREEN against the same string the order used.
export const SUPPORT_VIEW_LABEL = "View reports as member";

// ---------------------------------------------------------------------------
// D3-3 CLIENT — THE DOOR HAS EXACTLY THREE STATES, AND THE DASHBOARD MAY NOT INVENT A FOURTH.
//
// The client half of a second key is where second keys usually rot: a screen that decides for itself
// whether it is unlocked drifts from the server that actually decides, and the drift always goes the
// same direction — the screen thinks it is open. So the state is derived HERE, from the server's own
// status answer, by a function the gate runs, and the dashboard renders whatever comes back.
//
// ORDER MATTERS AND IS DELIBERATE: `unlocked` is read FIRST because the server minted the ticket and
// is the only thing that can say so. `passcodeSet` false is then the honest "there is no key yet"
// state, which is a DIFFERENT screen from "locked" — one asks her to make a key, the other to use it.
// ---------------------------------------------------------------------------
export function supportDoorState(status = {}) {
  if (status && status.unlocked) return "open";
  if (!status || !status.passcodeSet) return "unset";
  return "locked";
}

// ---------------------------------------------------------------------------
// R78 — **THE FOURTH STATE, AND IT IS NOT A DOOR. THIS IS THE ONE THAT WAS LIVE.**
//
// `supportDoorState` defaults an ABSENT status to "unset", which is right for the question it was
// written to answer: given what the server said, which door is this? But the panel asked it with
// `supportDoorState(status || {})` where `status` starts at `null` and was only ever assigned inside
// `if (d?.ok)`. So a status read that was REFUSED, DROPPED or simply had not answered yet drew the
// FIRST-TIME SETUP FORM over a door that has a passcode, under this module's own sentence *"No
// support passcode is set yet."* — on the privacy door in front of a member's readings.
//
// And it does not end at a wrong sentence. She fills that form in, saves, and `handleSupportPasscode`
// finds a passcode IS set and no `current` in the body, so the answer is **"Your current passcode is
// not right."** — over a form that never asked her for one, because the panel believed there wasn't
// one to ask about. R18's distinction at a door: "nobody has ever set this" and "we could not ask"
// are two facts, and the second one is not a state this door has.
//
// `supportDoorState` is deliberately UNCHANGED — it is still total over every status the server can
// send, and the fixture still runs it that way. What is added is the question the panel actually has
// to ask, which is one step earlier: do we know yet?
// ---------------------------------------------------------------------------
export const PASSCODE_ASKING = "Checking the support door...";
export const PASSCODE_UNREAD = "The support door could not be read just now. Nothing about it has changed, and this is not asking you to set a passcode.";

export function supportDoorView(status) {
  return status ? supportDoorState(status) : "unknown";
}

export const SUPPORT_DOOR_COPY = {
  unset: { heading: "Support passcode", message: PASSCODE_UNSET, action: "Save passcode" },
  locked: { heading: "Support view", message: PASSCODE_PROMPT, action: "Unlock" },
  open: { heading: "Support view", message: READ_ONLY_NOTE, action: SUPPORT_VIEW_LABEL },
  // **NO ACTION, AND THAT IS THE POINT OF IT.** Every other state offers her a button; this one has
  // nothing to offer because the house does not yet know what it is looking at, and a button here
  // would be exactly the setup form that was wrong. Declared by name in the fixture's roster with
  // that reason rather than excused by a hand-typed list of the other three (R28/R29/R69).
  unknown: { heading: "Support view", message: PASSCODE_ASKING, action: "" },
};

export function supportDoorCopy(status = {}) {
  return SUPPORT_DOOR_COPY[supportDoorState(status)];
}

// What the panel prints, which is `supportDoorCopy` one question earlier: an unread door gets the
// unknown copy rather than the copy of whichever door it might turn out to be.
export function supportDoorViewCopy(status) {
  return SUPPORT_DOOR_COPY[supportDoorView(status)];
}

// ---------------------------------------------------------------------------
// D3-2 ON THE SCREEN — THE WALLS, SAID OUT LOUD IN HER LANGUAGE.
//
// The server sends the forbidden list down with every support payload so the panel can print it. A
// raw slug on screen ("lala-conversation") is a wall she has to decode; these are the human names.
// `wallLabel` falls back to the slug, which is honest, and the gate asserts NO wall ever needs that
// fallback — so a wall added next year gets a name instead of quietly appearing as machinery.
// ---------------------------------------------------------------------------
export const WALLS_HEADING = "This view will never open:";
export const WALL_LABELS = {
  journal: "journal entries",
  notepaper: "notepaper",
  "lala-conversation": "Ask Lala conversations",
  bookmarks: "bookmarks",
  "saved-verses": "saved verses",
  "shelf-notes": "notes on shelf items",
  "birth-full": "the full birth date and time",
};

export function wallLabel(key) {
  return WALL_LABELS[String(key || "")] || String(key || "");
}

export function wallsSentence(walls = SUPPORT_FORBIDDEN) {
  const named = (Array.isArray(walls) ? walls : []).map(wallLabel).filter(Boolean);
  if (!named.length) return "";
  return `${WALLS_HEADING} ${named.join(", ")}.`;
}
